import { BAG_SIZE, EQUIP_SLOTS, type Race, WHEEL_TIERS, type WheelTierId } from "@two-crowns/data";
import { z } from "zod";
import { CHAT_MAX_LEN } from "./chat";
import { NAME_MAX, NAME_MIN } from "./constants";

/** Client -> server message type names. Every one has a Zod schema below; unknown fields are rejected. */
export const ClientMsg = {
  Move: "move",
  SelectTarget: "select_target",
  Attack: "attack",
  UseSkill: "use_skill",
  AllocateStats: "alloc_stats",
  ResetStats: "reset_stats",
  Pickup: "pickup",
  LootOpen: "loot_open",
  LootTake: "loot_take",
  InvMove: "inv_move",
  InvUse: "inv_use",
  InvUnequip: "inv_unequip",
  InvDestroy: "inv_destroy",
  /** The endless red (HP) or blue (MP) potion every character carries. */
  Drink: "drink",
  /** Ends one of your scroll buffs early (double-click on it). */
  CancelScroll: "cancel_scroll",
  /** Space: a small hop, only for show (no height in the game rules). */
  Jump: "jump",
  /** Market Scroll: search every stall of your nation, and buy from the list. */
  MarketSearch: "market_search",
  MarketBuy: "market_buy",
  /** Friend list: ask for it, add or remove a name. */
  FriendsGet: "friends_get",
  FriendAdd: "friend_add",
  FriendRemove: "friend_remove",
  ShopBuy: "shop_buy",
  ShopSell: "shop_sell",
  RewardClaim: "reward_claim",
  Upgrade: "upgrade",
  /** Merge identical jewelry into one piece a level higher. */
  JewelryMerge: "jewelry_merge",
  Chat: "chat",
  /** A private line to one player by name. */
  Whisper: "whisper",
  Town: "town",
  Portal: "portal",
  PartyInvite: "party_invite",
  PartyLeave: "party_leave",
  PartyKick: "party_kick",
  ClanCreate: "clan_create",
  ClanInvite: "clan_invite",
  ClanLeave: "clan_leave",
  ClanKick: "clan_kick",
  ClanRank: "clan_rank",
  ClanDisband: "clan_disband",
  /** Accept or decline the pending party or clan invite. */
  InviteReply: "invite_reply",
  StoreBuy: "store_buy",
  StoreTopup: "store_topup",
  /** Reads the account's gems again (back from a Polar checkout in another tab). */
  StoreRefresh: "store_refresh",
  CosmeticEquip: "cosmetic_equip",
  StallOpen: "stall_open",
  StallClose: "stall_close",
  StallView: "stall_view",
  StallBuy: "stall_buy",
  TradeRequest: "trade_request",
  TradeRespond: "trade_respond",
  TradeOffer: "trade_offer",
  TradeReady: "trade_ready",
  TradeCancel: "trade_cancel",
  /** A GM chat command ("/ban Name 24"). */
  GmCommand: "gm_command",
  WarehouseOpen: "wh_open",
  WarehouseDeposit: "wh_deposit",
  WarehouseWithdraw: "wh_withdraw",
  WarehouseGold: "wh_gold",
  /** Quest log: take, hand in or drop a quest (anywhere, no quest giver). */
  QuestAccept: "quest_accept",
  QuestComplete: "quest_complete",
  QuestAbandon: "quest_abandon",
  /** Break a Riftstone at the gemcutter (the reel starts; the server decides the prize). */
  WheelSpin: "wheel_spin",
  /** Takes today's login reward (once per account per day). */
  DailyClaim: "daily_claim",
  /** Takes the reward of a first week goal this character reached. */
  GoalClaim: "goal_claim",
  /** The mailbox: a letter opened (read), its attachments taken, or the letter thrown away. */
  MailRead: "mail_read",
  MailClaim: "mail_claim",
  MailDelete: "mail_delete",
  /** The Crystal Clash: sign up while sign-up is open, take it back, or leave a match early. */
  ClashJoin: "clash_join",
  ClashCancel: "clash_cancel",
  ClashExit: "clash_exit",
} as const;

/** Server -> client message names. */
export const ServerMsg = {
  /** Batched GameEvent[] for one tick. */
  Events: "ev",
  SkillError: "skill_error",
  /** Owner-only full inventory snapshot, sent when it changes. */
  Inventory: "inv",
  /** Owner-only short feedback (bag full, too far, +gold). */
  Notice: "notice",
  /** Contents of a loot bag the player opened (lootId + null = the bag is gone). */
  LootContents: "loot_contents",
  /** Owner-only result of an upgrade attempt. */
  UpgradeResult: "upgrade_result",
  SystemMessage: "system_message",
  /** One global chat line, broadcast to everyone. */
  Chat: "chat",
  /** You are being moved to another zone: reconnect there. */
  ZoneChange: "zone_change",
  /** Your party (null = not in one). */
  Party: "party",
  /** Your clan (null = not in one). */
  Clan: "clan",
  /** A page of the market (MarketResultsMessage). */
  MarketResults: "market_results",
  /** Your friend list with who is online and where (FriendsMessage). */
  Friends: "friends",
  /** Someone invites you to their party or clan. */
  Invite: "invite",
  /** Your account's gems, premium and skins (owner only). */
  Account: "account",
  /** A player's stall you opened (what is still for sale). */
  Stall: "stall",
  /** Your trade window (null = closed). */
  Trade: "trade",
  /** Someone asks you to trade. */
  TradeInvite: "trade_invite",
  /** Your warehouse (after opening it at a keeper, and after every change). */
  Warehouse: "warehouse",
  /** The owner's quest log (sent when it changes). */
  Quests: "quests",
  /** What the Riftstone Reel stops on and what it paid. */
  WheelResult: "wheel_result",
  /** In a timed zone (the Rift): when this player's time there runs out. */
  Rift: "rift",
  /** Server-wide bonus events a GM started (XP, drops) and when they end. */
  ServerEvents: "server_events",
  /** The war of Morgard: open or not, when, and which nation holds the victory bonus. */
  War: "war",
  /** The login reward calendar and the first week goals (RewardsMessage), owner only. */
  Rewards: "rewards",
  /** The account's mailbox (MailMessage), owner only, sent when it changes. */
  Mail: "mail",
  /** The Crystal Clash for this player (ClashMessage): sign-up, match, penalty. */
  Clash: "clash",
  /** In a Crystal Clash arena: the match's score (ClashScoreMessage). */
  ClashScore: "clash_score",
} as const;

export const CharacterNameSchema = z
  .string()
  .min(NAME_MIN)
  .max(NAME_MAX)
  .regex(/^[A-Za-z0-9]+$/, "letters and digits only");

export const JoinOptionsSchema = z.strictObject({
  token: z.string().min(1),
  /** Must belong to the token's account; everything else is loaded from the database. */
  characterId: z.string().uuid(),
  /** Game server and channel: pick the room together with the zone. */
  serverId: z.string().regex(/^[a-z0-9_]+$/),
  channel: z.int().min(1).max(9),
  /** Picks the zone room; must match the zone the character is saved in. */
  zoneId: z
    .string()
    .regex(/^[a-z0-9_]+$/)
    .optional(),
  /** A granted ticket from the server's line (GET /queue on the game server). */
  ticket: z.string().max(64).optional(),
});
export type JoinOptions = z.infer<typeof JoinOptionsSchema>;

/**
 * One movement input. Each input advances the player by exactly `movement.inputStepMs`
 * on the server, so the client can never claim more elapsed time than it was given.
 */
export const MoveMessageSchema = z.strictObject({
  seq: z.int().min(0),
  dir: z.strictObject({
    x: z.number().min(-1).max(1),
    z: z.number().min(-1).max(1),
  }),
  t: z.number(),
});
export type MoveMessage = z.infer<typeof MoveMessageSchema>;

const EntityIdSchema = z.string().min(1).max(64);
const PointSchema = z.strictObject({ x: z.number(), z: z.number() });

export const SelectTargetMessageSchema = z.strictObject({
  targetId: EntityIdSchema.nullable(),
});
export type SelectTargetMessage = z.infer<typeof SelectTargetMessageSchema>;

/** Auto-attack on or off (R starts it, moving stops it); the target stays selected. */
export const AttackMessageSchema = z.strictObject({ on: z.boolean() });

/** Intent only: the server decides range, cooldown, MP, hit and damage. */
export const UseSkillMessageSchema = z.strictObject({
  skillId: z.string().min(1).max(64),
  targetId: EntityIdSchema.optional(),
  point: PointSchema.optional(),
});
export type UseSkillMessage = z.infer<typeof UseSkillMessageSchema>;

const StatAmount = z.int().min(0).max(1000).optional();
/** Spend free stat points. The server checks the total against the character's free points. */
export const AllocateStatsMessageSchema = z.strictObject({
  str: StatAmount,
  dex: StatAmount,
  int: StatAmount,
  vit: StatAmount,
});
export type AllocateStatsMessage = z.infer<typeof AllocateStatsMessageSchema>;
/** Take back every spent stat point (the class's starting stats remain). */
export const ResetStatsMessageSchema = z.strictObject({});

export type SkillErrorReason =
  | "unknown_skill"
  | "not_learned"
  | "dead"
  | "incapacitated"
  | "casting"
  | "gcd"
  | "cooldown"
  | "mp"
  | "no_target"
  | "invalid_target"
  | "range"
  /** A run skill while a Speed Scroll runs: the two do not add up. */
  | "speed_scroll";

export interface SkillErrorMessage {
  skillId: string;
  reason: SkillErrorReason;
}

/** Server -> client combat events, batched per tick. Short keys keep the payload small. */
export type GameEvent =
  /** Damage or heal. k: hit | miss | dot | heal. c = crit. */
  | { e: "dmg"; s: string; t: string; a: number; k: "hit" | "miss" | "dot" | "heal"; c?: 1 }
  /** Auto-attack swing. */
  | { e: "swing"; s: string; t: string }
  /** Cast bar started. */
  | { e: "cast"; c: string; sk: string; ms: number }
  | { e: "cancel"; c: string }
  /** Skill went off (instant, or cast completed). */
  | { e: "fire"; c: string; sk: string; t?: string; p?: { x: number; z: number } }
  | { e: "death"; id: string }
  /** A boss winds up an attack: the area (circle, cone or ring) is marked for ms. */
  | {
      e: "bossCast";
      s: string;
      ab: string;
      kind: "slam" | "cleave" | "nova" | "meteor";
      ms: number;
      p: { x: number; z: number };
      r: number;
      rot: number;
    }
  /** The boss attack lands. */
  | {
      e: "bossHit";
      s: string;
      ab: string;
      kind: "slam" | "cleave" | "nova" | "meteor";
      p: { x: number; z: number };
      r: number;
      rot: number;
    }
  /** A player (s) slew a player of the other nation (t). */
  | { e: "pvpKill"; s: string; t: string; np: number; lost: number }
  /** XP gained by player `id`. */
  | { e: "xp"; id: string; a: number }
  /** Player `id` reached level `lv`. */
  | { e: "level"; id: string; lv: number }
  | { e: "respawn"; id: string }
  /** Player `id` drank their red (hp) or blue (mp) potion and got `a` back. */
  | { e: "potion"; id: string; k: "hp" | "mp"; a: number }
  /** Player `id` started returning to town; arrives after `ms` unless hit. */
  | { e: "recall"; id: string; ms: number }
  /** The trip ended: ok = arrived in town, otherwise interrupted. */
  | { e: "recallEnd"; id: string; ok: boolean }
  /** A cleanse took the harmful effects off `id`. */
  | { e: "cleanse"; id: string }
  /** A player hopped (Space): only a look, everyone nearby sees it. */
  | { e: "jump"; id: string }
  /** A base fire tower at (x, z) shot `t`, a player of the other nation. */
  | { e: "tower"; x: number; z: number; t: string }
  /** `id` was brought to `by` by a party summon. */
  | { e: "summoned"; id: string; by: string };

const BagIndex = z
  .int()
  .min(0)
  .max(BAG_SIZE - 1);
const EquipSlotSchema = z.enum(EQUIP_SLOTS);
const SlotRefSchema = z.union([
  z.strictObject({ bag: BagIndex }),
  z.strictObject({ equip: EquipSlotSchema }),
]);

export const PickupMessageSchema = z.strictObject({ lootId: z.string().min(1).max(32) });
export const LootOpenMessageSchema = z.strictObject({ lootId: z.string().min(1).max(32) });
export const LootTakeMessageSchema = z.strictObject({
  lootId: z.string().min(1).max(32),
  /** Item index in the bag, "gold", or "all". */
  what: z.union([z.int().min(0).max(31), z.literal("gold"), z.literal("all")]),
});

export interface LootContentsMessage {
  lootId: string;
  /** null when the bag is empty/gone. */
  gold: number | null;
  items: ItemView[];
}
export const InvMoveMessageSchema = z.strictObject({ from: SlotRefSchema, to: SlotRefSchema });
export const InvUseMessageSchema = z.strictObject({ bag: BagIndex });
export const InvUnequipMessageSchema = z.strictObject({ equip: EquipSlotSchema });
export const InvDestroyMessageSchema = z.strictObject({ bag: BagIndex });
export const JumpMessageSchema = z.strictObject({});
export const CancelScrollMessageSchema = z.strictObject({
  item: z
    .string()
    .regex(/^[a-z0-9_]+$/)
    .max(64),
});
export const DrinkMessageSchema = z.strictObject({ kind: z.enum(["hp", "mp"]) });

export const QuestMessageSchema = z.strictObject({
  quest: z
    .string()
    .max(64)
    .regex(/^[a-z0-9_]+$/),
});
export type QuestMessage = z.infer<typeof QuestMessageSchema>;

export const WheelSpinMessageSchema = z.strictObject({
  npc: z
    .string()
    .max(64)
    .regex(/^[a-z0-9_]+$/),
  tier: z.enum(WHEEL_TIERS),
});
export type WheelSpinMessage = z.infer<typeof WheelSpinMessageSchema>;

export interface WheelResultMessage {
  tier: WheelTierId;
  /** The prize the reel stops on (index into the tier's prizes). */
  index: number;
  /** The stone was empty. */
  empty?: boolean;
  gold?: number;
  item?: {
    templateId: string;
    name: string;
    grade: "common" | "magic" | "rare" | "unique";
    qty: number;
  };
}

/** Bonus events (XP, drops) and a GM opening of the Rift Gate outside its hours. */
export type ServerEventKind = "xp" | "drop" | "gold" | "rift";
/** Active events: percent bonus (0 for the Rift), start and end time (server ms) per kind. */
export type ServerEventsMessage = Partial<
  Record<ServerEventKind, { pct: number; from: number; until: number }>
>;

/** The war of Morgard, as everyone sees it (the coordinator's hub decides it). */
export interface WarMessage {
  /** Whether players may go in now. */
  open: boolean;
  /** While open: when this war began (ms); it names the war a fallen keeper belongs to. */
  openedAt: number;
  /** While open: when it ends (ms). */
  closesAt: number;
  /** When the next scheduled war begins (ms). */
  next: number;
  /** The nation that won the latest war, while its bonus lasts (until the next war begins). */
  winner: Race | null;
  /** The winners' bonus in percent, while it lasts. */
  bonus: { xp: number; gold: number; drop: number } | null;
}

/** The Crystal Clash as one player sees it (the coordinator's hub sends it when it changes). */
export interface ClashMessage {
  /** "signup" until `startsAt`, "running" until `endsAt`, otherwise "idle". */
  phase: "idle" | "signup" | "running";
  /** The current or next start (ms). */
  startsAt: number;
  /** While running: when the matches end at the latest (ms). */
  endsAt: number;
  /** This character is signed up for (or playing) the current event. */
  joined: boolean;
  /** The account may not join until then (ms; 0 = free). */
  bannedUntil: number;
  /** Early exits left today without a penalty. */
  exitsLeft: number;
}

/** In a Crystal Clash arena: the match's score, sent when it changes. */
export interface ClashScoreMessage {
  kills: Record<Race, number>;
  killsToWin: number;
  /** When the match ends at the latest (ms). */
  endsAt: number;
  /** The winning nation, a draw, or null while it goes on. */
  winner: Race | "draw" | null;
  /** Once decided: when everyone is sent back (ms). */
  backAt: number;
}

export interface RiftMessage {
  /** Server time (ms) when this player is sent back. */
  until: number;
}

/** Server -> owner: progress of open quests, one-time quests done, dailies done today. */
export const DailyClaimMessageSchema = z.strictObject({});
/** One letter of the mailbox, by its id. */
export const MailIdMessageSchema = z.strictObject({ id: z.int().positive() });

/** An item sent with a letter (a rental when `days` is set). */
export interface MailItem {
  item: string;
  qty: number;
  plus?: number;
  days?: number;
}

/**
 * A letter from the game team (operator, `pnpm admin mail`): a subject and text, in Turkish
 * too when written so, and what comes with it (items, gold for the character who takes it,
 * gems for the account).
 */
export interface MailView {
  id: number;
  from: string;
  subject: string;
  body: string;
  subjectTr?: string;
  bodyTr?: string;
  items: MailItem[];
  gold: number;
  gems: number;
  /** Sent and gone (ms since epoch); unclaimed attachments go with it. */
  at: number;
  expiresAt: number;
  read: boolean;
  /** Its attachments were taken (by any character of the account). */
  claimed: boolean;
}

export interface MailMessage {
  mails: MailView[];
}
export const GoalClaimMessageSchema = z.strictObject({
  goal: z
    .string()
    .regex(/^[a-z0-9_]+$/)
    .max(32),
});

/**
 * The reward window: the login calendar (`economy.dailyReward`) and this character's first
 * week goals (`economy.firstWeek`). `daily` is null for a GM, `goals` once the week is over.
 */
export interface RewardsMessage {
  daily: {
    /** Login rewards the account has taken so far (the calendar's day is this modulo its length). */
    count: number;
    /** Today's is taken already. */
    claimedToday: boolean;
    /** When the next day begins (ms since epoch). */
    nextAt: number;
  } | null;
  goals: {
    /** When the first week ends (ms since epoch). */
    until: number;
    /** Each goal's progress, up to its target. */
    progress: Record<string, number>;
    claimed: string[];
  } | null;
}

export interface QuestsMessage {
  active: Record<string, number>;
  done: string[];
  /** Daily quests already handed in today. */
  doneToday: string[];
}

export interface ItemView {
  id: string;
  templateId: string;
  grade: "common" | "magic" | "rare" | "unique";
  plus: number;
  rolls: { stat: string; value: number }[];
  qty: number;
  /** Rented (store) items: gone at this time, ms since epoch. */
  expiresAt?: number;
}

export interface InventoryMessage {
  gold: number;
  /** National Points balance (spent at the Colony quartermasters). */
  np: number;
  bag: (ItemView | null)[];
  equip: Partial<Record<(typeof EQUIP_SLOTS)[number], ItemView>>;
}

export interface NoticeMessage {
  code: string;
  text?: string;
}

const NpcId = z
  .string()
  .regex(/^[a-z0-9_]+$/)
  .max(32);
export const ShopBuyMessageSchema = z.strictObject({
  npc: NpcId,
  item: z
    .string()
    .regex(/^[a-z0-9_]+$/)
    .max(48),
  qty: z.int().min(1).max(50),
});
export const ShopSellMessageSchema = z.strictObject({ npc: NpcId, bag: BagIndex });
/** The Herald: a ranking reward (pvp.json rankRewards id) to the bag. */
export const RewardClaimMessageSchema = z.strictObject({
  npc: NpcId,
  reward: z
    .string()
    .regex(/^[a-z0-9_]+$/)
    .max(32),
});
export const UpgradeMessageSchema = z.strictObject({
  npc: NpcId,
  bag: BagIndex,
  /** Not needed on the first, gold-only steps. */
  scroll: BagIndex.optional(),
  /** A Fortune Stone in the bag: better odds for this roll. */
  stone: BagIndex.optional(),
});

export const JewelryMergeMessageSchema = z.strictObject({
  npc: NpcId,
  bag: BagIndex,
  scroll: BagIndex,
});

export interface UpgradeResultMessage {
  itemName: string;
  grade: "common" | "magic" | "rare" | "unique";
  from: number;
  to: number;
  success: boolean;
  destroyed: boolean;
  chancePct: number;
  maxPlus: number;
  usedStone: boolean;
}

export interface SystemMessage {
  text: string;
  /** Banner only: the chat line comes separately (server news in color). */
  bannerOnly?: boolean;
  /** A GM notice: scrolls across the top of the screen instead of a banner. */
  ticker?: boolean;
  /** With ticker: an event (a GM dropped event monsters), scrolled along the bottom. */
  event?: boolean;
}

/** Raw text is cleaned and filtered on the server; the limit here only bounds the payload. */
export const CHAT_CHANNELS = ["global", "party", "clan"] as const;
export type ChatChannel = (typeof CHAT_CHANNELS)[number];

export const ChatSendMessageSchema = z.strictObject({
  text: z.string().min(1).max(CHAT_MAX_LEN),
  channel: z.enum(CHAT_CHANNELS).default("global"),
});
export type ChatSendMessage = z.infer<typeof ChatSendMessageSchema>;

export interface ChatMessage {
  from: string;
  race: Race;
  text: string;
  /** Server time (ms). */
  ts: number;
  /**
   * System lines: "pvp" = a PvP kill ("X defeated Y"), "notice" = a GM announcement,
   * "news" = server-wide news about a player (`from` did `text`; `item` is colored by grade).
   */
  kind?: "pvp" | "notice" | "news";
  item?: { name: string; grade: "common" | "magic" | "rare" | "unique" };
  /** Written by a game master (yellow, [GM] tag). */
  gm?: boolean;
  channel: ChatChannel | "whisper";
  /** Whispers only: set on the sender's own copy, the name it went to. */
  to?: string;
  /** PvP kills: both names, each drawn in its nation's color (killer first). */
  pvp?: { killer: string; killerRace: Race; victim: string; victimRace: Race };
}

// ---------------------------------------------------------------- parties and clans

const PlayerName = z.string().min(NAME_MIN).max(NAME_MAX);

export const WhisperMessageSchema = z.strictObject({
  to: PlayerName,
  text: z.string().min(1).max(CHAT_MAX_LEN),
});
export type WhisperMessage = z.infer<typeof WhisperMessageSchema>;

export const PartyInviteMessageSchema = z.strictObject({ name: PlayerName });
export const PartyKickMessageSchema = z.strictObject({ name: PlayerName });
export const EmptyMessageSchema = z.strictObject({});
export const ClanCreateMessageSchema = z.strictObject({ name: z.string().min(1).max(32) });
export const ClanInviteMessageSchema = z.strictObject({ name: PlayerName });
export const ClanKickMessageSchema = z.strictObject({ name: PlayerName });
export const ClanRankMessageSchema = z.strictObject({
  name: PlayerName,
  /** "leader" hands the clan over (the old leader becomes an officer). */
  rank: z.enum(["leader", "officer", "member"]),
});
export const InviteReplyMessageSchema = z.strictObject({
  kind: z.enum(["party", "clan"]),
  accept: z.boolean(),
});

export interface PartyMemberView {
  /** Character id. */
  id: string;
  name: string;
  cls: string;
  level: number;
  online: boolean;
  zoneId: string;
  /** Session id in that zone's room (to find the member in the synced state). */
  sid: string;
}

export interface PartyMessage {
  leader: string;
  members: PartyMemberView[];
  max: number;
}

export interface ClanMemberView {
  name: string;
  rank: "leader" | "officer" | "member";
  level: number;
  cls: string;
  online: boolean;
  /** Lifetime National Points (what they add to the clan's grade). */
  np: number;
}

export interface ClanMessage {
  name: string;
  /** Your own rank. */
  you: "leader" | "officer" | "member";
  members: ClanMemberView[];
  max: number;
  /** Clan grade: 5 for a new clan, up to 1. */
  grade: number;
  /** The members' lifetime National Points added up. */
  np: number;
  /** NP needed for the next grade (null at grade 1). */
  nextNp: number | null;
}

/** A friend as your list shows them. */
export interface FriendView {
  name: string;
  level: number;
  cls: string;
  online: boolean;
  /** Where they are, while online. */
  zoneId?: string;
}

export interface FriendsMessage {
  friends: FriendView[];
  max: number;
  /** A friend just came online or left: shown as a chat line. */
  notice?: { who: string; online: boolean };
}

/** Market Scroll search: the client matches item names in its language and sends the ids. */
export const MarketSearchMessageSchema = z.strictObject({
  templates: z
    .array(
      z
        .string()
        .regex(/^[a-z0-9_]+$/)
        .max(64),
    )
    .max(400)
    .optional(),
  sort: z.enum(["price_asc", "price_desc", "new"]),
  page: z.int().min(0).max(1000),
});
export const MarketBuyMessageSchema = z.strictObject({
  itemId: z.string().min(1).max(64),
  /** The price the buyer saw: a changed price refuses the sale. */
  price: z.int().min(1).max(2_100_000_000),
});

export interface MarketRow {
  itemId: string;
  item: ItemView;
  price: number;
  seller: string;
  zoneId: string;
  /** The stall stands on its own (its owner logged out). */
  offline: boolean;
  /** Your own item (you cannot buy it). */
  mine: boolean;
}

export interface MarketResultsMessage {
  rows: MarketRow[];
  page: number;
  pages: number;
  total: number;
}

export const FriendNameMessageSchema = z.strictObject({ name: PlayerName });

export const StoreBuyMessageSchema = z.strictObject({ id: z.string().regex(/^[a-z0-9_]+$/) });
export const StoreTopupMessageSchema = z.strictObject({ pack: z.string().regex(/^[a-z0-9_]+$/) });
export const StoreRefreshMessageSchema = z.strictObject({});
/** Wear an armor skin; id "" takes it off. (Glows are items, worn from the inventory.) */
export const CosmeticEquipMessageSchema = z.strictObject({
  kind: z.enum(["skin"]),
  id: z.string().max(40),
});

const ItemIdSchema = z.string().min(1).max(64);
const Gold = z.int().min(0).max(2_100_000_000);

export const StallOpenMessageSchema = z.strictObject({
  title: z.string().max(32),
  listings: z
    .array(z.strictObject({ itemId: ItemIdSchema, price: Gold.min(1) }))
    .min(1)
    .max(24),
});
export const StallViewMessageSchema = z.strictObject({ sellerId: z.string().min(1).max(64) });
export const StallBuyMessageSchema = z.strictObject({
  sellerId: z.string().min(1).max(64),
  itemId: ItemIdSchema,
});
export const TradeRequestMessageSchema = z.strictObject({ targetId: z.string().min(1).max(64) });
export const TradeRespondMessageSchema = z.strictObject({ accept: z.boolean() });
export const TradeOfferMessageSchema = z.strictObject({
  items: z.array(ItemIdSchema).max(16),
  gold: Gold,
});

export const GmCommandMessageSchema = z.strictObject({ text: z.string().min(2).max(300) });

export const WarehouseOpenMessageSchema = z.strictObject({ npc: NpcId });
export const WarehouseDepositMessageSchema = z.strictObject({
  npc: NpcId,
  bag: z.int().min(0).max(63),
});
export const WarehouseWithdrawMessageSchema = z.strictObject({
  npc: NpcId,
  slot: z.int().min(0).max(255),
});
/** Positive stores gold, negative takes it back. */
export const WarehouseGoldMessageSchema = z.strictObject({
  npc: NpcId,
  amount: z.int().min(-2_100_000_000).max(2_100_000_000),
});

export interface WarehouseMessage {
  items: (ItemView | null)[];
  gold: number;
}

/** The entity that stands at a character's stall (their stall-keeper), online or not. */
export const stallIdOf = (characterId: string) => `ghost:${characterId}`;
/** A stall-keeper: drawn as the stall alone, no body. */
export const isStallKeeper = (entityId: string) => entityId.startsWith("ghost:");

export interface StallMessage {
  sellerId: string;
  seller: string;
  title: string;
  listings: { item: ItemView; price: number }[];
  /** Your own stall: you may close it, not buy from it. */
  mine: boolean;
}

export interface TradeMessage {
  partner: string;
  partnerId: string;
  /** Your offer: bag item ids. */
  mine: { items: string[]; gold: number; ready: boolean };
  /** Their offer, as full items. */
  theirs: { items: ItemView[]; gold: number; ready: boolean };
}

export interface TradeInviteMessage {
  from: string;
  fromId: string;
}

export interface AccountMessage {
  gems: number;
  /** ms since epoch; 0 = none. */
  premiumUntil: number;
  /** Armor skins still running. */
  skins: string[];
  /** Each skin bought to the end of its time (ms since epoch), running or over. */
  skinUntil: Record<string, number>;
  /** Real-money packs work (false: dev credits them for free, production says soon). */
  paymentsLive: boolean;
  devTopup: boolean;
}

export interface InviteMessage {
  kind: "party" | "clan";
  from: string;
  /** Clan invites: the clan's name. */
  clan?: string;
}

export const TownMessageSchema = z.strictObject({});

export const PortalMessageSchema = z.strictObject({
  index: z.int().min(0).max(31),
  /** Destination zone id, picked from the gate's list. */
  to: z.string().regex(/^[a-z0-9_]{1,32}$/),
});

export interface ZoneChangeMessage {
  zoneId: string;
  /** Join this channel there (a GM summon to the GM's channel); else the current one. */
  channel?: number;
}
